import React, { useMemo } from "react";
import moment from "moment";
import { FaArrowDown, FaArrowUp } from "react-icons/fa";
import { formatDateTime } from "../../utils/dateTime";

const fmtRs = (n) =>
  Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 });

const formatDate = (row) => {
  if (!row?.date || row.date === "-") return "—";
  if (typeof row.date === "number") {
    return formatDateTime(row.date);
  }
  return String(row.date);
};

const isTotalRow = (row) => row?.desc === "Total";

const buildSubtitle = (row) => {
  const parts = [];
  if (row.invoice && row.invoice !== "-") parts.push(`Inv ${row.invoice}`);
  if (row.name && row.name !== "-") parts.push(row.name);
  if (row.desc && row.desc !== "-" && row.desc !== "Total") parts.push(row.desc);
  if (row.qty != null && row.qty !== "-") {
    parts.push(`Qty ${row.qty}`);
  }
  if (row.price != null && row.price !== "-") {
    parts.push(`@ ${fmtRs(row.price)}`);
  }
  return parts.join(" · ") || "Ledger entry";
};

const LedgerTotalCard = ({ row }) => (
  <article className="rounded-2xl bg-main text-white shadow-card overflow-hidden font-quicksand p-4">
    <p className="text-xs font-bold uppercase tracking-wide opacity-80">Total</p>
    <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
      <div>
        <p className="text-white/70 text-xs">Credit (CR)</p>
        <p className="font-bold tabular-nums text-emerald-200">
          Rs {fmtRs(row.cr)}
        </p>
      </div>
      <div>
        <p className="text-white/70 text-xs">Debit (DR)</p>
        <p className="font-bold tabular-nums text-rose-200">
          Rs {fmtRs(row.dr)}
        </p>
      </div>
      {row.qty != null && row.qty !== "-" && (
        <div>
          <p className="text-white/70 text-xs">Qty</p>
          <p className="font-bold tabular-nums">{fmtRs(row.qty)}</p>
        </div>
      )}
      {row.balance !== "-" && row.balance != null && (
        <div>
          <p className="text-white/70 text-xs">Net balance</p>
          <p className="font-bold tabular-nums">Rs {fmtRs(row.balance)}</p>
        </div>
      )}
    </div>
  </article>
);

const LedgerEntryCard = ({ row }) => {
  const cr = Number(row.cr || 0);
  const dr = Number(row.dr || 0);
  const isCredit = cr > 0 && dr <= 0;
  const isDebit = dr > 0 && cr <= 0;
  const amount = isCredit ? cr : isDebit ? dr : Math.max(cr, dr);
  const subtitle = buildSubtitle(row);

  return (
    <article className="rounded-2xl bg-card shadow-[0_1px_6px_rgba(0,0,0,0.08)] overflow-hidden font-quicksand">
      <div className="px-4 pt-4 pb-3 flex items-start gap-3">
        <div
          className={`shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
            isCredit ? "bg-successSoft" : isDebit ? "bg-dangerSoft" : "bg-subtle"
          }`}
        >
          {isCredit ? (
            <FaArrowDown className="text-success text-base" aria-hidden />
          ) : isDebit ? (
            <FaArrowUp className="text-danger text-base" aria-hidden />
          ) : (
            <span className="text-muted text-xs font-bold">—</span>
          )}
        </div>

        <div className="flex-1 min-w-0 pt-0.5">
          {amount > 0 && (
            <p
              className={`text-xl font-extrabold tabular-nums leading-none ${
                isCredit
                  ? "text-success"
                  : isDebit
                  ? "text-danger"
                  : "text-ink"
              }`}
            >
              Rs {fmtRs(amount)}
              {isCredit && (
                <span className="text-xs font-semibold ml-2 text-success">
                  CR
                </span>
              )}
              {isDebit && (
                <span className="text-xs font-semibold ml-2 text-danger">
                  DR
                </span>
              )}
            </p>
          )}
          <p className="text-sm text-ink mt-2 leading-snug line-clamp-2">
            {subtitle}
          </p>
        </div>
      </div>

      <div className="mx-4 border-t border-line" />

      <div className="px-4 py-3 flex items-center justify-between gap-3 text-sm text-muted">
        <span className="shrink-0">{formatDate(row)}</span>
        {row.balance !== "-" && row.balance != null && (
          <span className="tabular-nums font-medium text-right truncate">
            Balance: Rs {fmtRs(row.balance)}
          </span>
        )}
      </div>
    </article>
  );
};

const LedgerEntryCards = ({
  rows = [],
  emptyMessage = "No ledger entries for this filter.",
}) => {
  const { entries, totalRow } = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    const total = list.find(isTotalRow);
    const entries = list.filter((r) => !isTotalRow(r));
    return { entries, totalRow: total || null };
  }, [rows]);

  if (!entries.length && !totalRow) {
    return (
      <div className="text-center py-12 rounded-xl border border-dashed border-line text-muted text-sm">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-2xl bg-subtle p-3 font-quicksand">
      {entries.map((row, index) => (
        <LedgerEntryCard key={`${row.invoice}-${row.date}-${index}`} row={row} />
      ))}
      {totalRow && <LedgerTotalCard row={totalRow} />}
    </div>
  );
};

export default LedgerEntryCards;
